import {
  forwardRef,
  useMemo,
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import { useFieldControl } from "../Field/FieldContext";
import { SliderMarks } from "./SliderMarks";
import {
  ControlFrame,
  Rail,
  RangeInputStack,
  Root,
  StackedRangeInput,
  ValueLabel,
} from "./sliderStyles";
import { useRangeSliderInteraction } from "./useRangeSliderInteraction";
import {
  clamp,
  DEFAULT_MAX,
  DEFAULT_MIN,
  formatSliderValue,
  getGeneratedMarks,
  normalizeBounds,
  percentFromValue,
  thumbCenter,
  type RangeSliderThumb,
  type SliderMark,
  type SliderSize,
  type SliderValueLabelDisplay,
  type SliderVariant,
} from "./sliderMath";

export type { RangeSliderThumb } from "./sliderMath";

export interface RangeSliderProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | "type"
  | "value"
  | "defaultValue"
  | "min"
  | "max"
  | "step"
  | "size"
  | "name"
  | "onChange"
  | "aria-label"
> {
  value: readonly [number, number];
  onValueChange?: (
    value: [number, number],
    event: ChangeEvent<HTMLInputElement>,
    activeThumb: RangeSliderThumb,
  ) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  min?: number;
  max?: number;
  step?: number | "any";
  /**
   * 두 thumb 사이 최소 간격. 0(기본)이면 맞닿음(겹침) 허용.
   * 0보다 크면 그 간격을 유지하며, 어느 경우에도 교차(swap)는 막는다.
   */
  minDistance?: number;
  variant?: SliderVariant;
  trackSize?: SliderSize;
  thumbSize?: SliderSize;
  /**
   * tick 표시. `true`면 `step` 간격마다 tick 생성(점 = snap 정지점과 일치). 배열이면 지정 위치에만
   * tick/label. `step`(snap 간격)과 marks(표시)는 독립이라, 점을 snap과 맞추려면 `true`를 쓴다.
   */
  marks?: boolean | readonly SliderMark[];
  valueLabelDisplay?: SliderValueLabelDisplay;
  formatValue?: (value: number, activeThumb: RangeSliderThumb) => ReactNode;
  getAriaValueText?: (value: number, activeThumb: RangeSliderThumb) => string;
  startAriaLabel?: string;
  endAriaLabel?: string;
  startName?: string;
  endName?: string;
  fullWidth?: boolean;
}

/**
 * Two-thumb range slider.
 * 라벨이 필요하면 `<Field>`로 감싸세요 — 자체 `label` prop은 없습니다.
 */
export const RangeSlider = forwardRef<HTMLInputElement, RangeSliderProps>(
  (
    {
      value,
      onValueChange,
      onChange,
      min: minProp = DEFAULT_MIN,
      max: maxProp = DEFAULT_MAX,
      step,
      minDistance = 0,
      variant = "continuous",
      trackSize = "md",
      thumbSize = "md",
      marks = false,
      valueLabelDisplay = "off",
      formatValue = formatSliderValue,
      getAriaValueText,
      startAriaLabel = "Minimum value",
      endAriaLabel = "Maximum value",
      startName,
      endName,
      fullWidth = true,
      disabled = false,
      className,
      style,
      id,
      ...rest
    },
    ref,
  ) => {
    const {
      controlId,
      labelId,
      disabled: isDisabled,
    } = useFieldControl({
      componentName: "RangeSlider",
      id,
      disabled,
    });
    const { min, max } = normalizeBounds(minProp, maxProp);
    const startValue = clamp(Math.min(value[0], value[1]), min, max);
    const endValue = clamp(Math.max(value[0], value[1]), min, max);
    const resolvedStep = step ?? (variant === "discrete" ? 1 : "any");
    const startPercent = percentFromValue(startValue, min, max);
    const endPercent = percentFromValue(endValue, min, max);
    const startProgress = thumbCenter(startPercent);
    const endProgress = thumbCenter(endPercent);
    const resolvedMarks = useMemo(
      () => getGeneratedMarks(marks, min, max, resolvedStep),
      [marks, max, min, resolvedStep],
    );
    const hasMarkLabels = resolvedMarks.some((mark) => mark.label !== undefined);
    const frameStyle = {
      ...style,
      "--c6-slider-mark-label-space": hasMarkLabels ? "1.3rem" : "0rem",
    } as CSSProperties;

    const emitChange = (
      nextValue: [number, number],
      event: ChangeEvent<HTMLInputElement>,
      activeThumb: RangeSliderThumb,
    ) => {
      onValueChange?.(nextValue, event, activeThumb);
      onChange?.(event);
    };

    // minDistance 만큼 간격 유지 + 교차 금지. 트랙 드래그도 input 이벤트로 이 경로를 탄다.
    // gap 을 트랙 폭으로 cap 하고, 들어온 value 가 이미 gap 을 위반(또는 minDistance>범위)해도
    // bound 가 역전돼 범위 밖 값이 emit 되지 않도록 lower/upper 를 [min,max] 안으로 보정한다.
    const gap = clamp(minDistance, 0, max - min);

    const handleStartChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const nextStart = clamp(
        Number(event.currentTarget.value),
        min,
        Math.max(min, endValue - gap),
      );
      emitChange([nextStart, endValue], event, "start");
    };

    const handleEndChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const nextEnd = clamp(
        Number(event.currentTarget.value),
        Math.min(max, startValue + gap),
        max,
      );
      emitChange([startValue, nextEnd], event, "end");
    };

    const { stackRef, endRef, setStartRef, handleTrackPointerDown } = useRangeSliderInteraction({
      min,
      max,
      startValue,
      endValue,
      resolvedStep,
      thumbSize,
      disabled: isDisabled,
      forwardedRef: ref,
    });

    const lowerThumbRaised = endPercent - startPercent < 8;

    return (
      <Root $fullWidth={fullWidth} className={className} style={frameStyle}>
        <ControlFrame
          // 두 thumb 는 하나의 labelable control 이 아니라 group — Field 라벨은
          // aria-labelledby 로 그룹 전체에 연결되고, 각 thumb 는 자체 aria-label 을 갖는다.
          role="group"
          aria-labelledby={labelId}
          $disabled={isDisabled}
          $trackSize={trackSize}
          $thumbSize={thumbSize}
          $valueLabelDisplay={valueLabelDisplay}
          // active-start/end(매 프레임 변동)는 inline CSS 변수로 — styled 템플릿 보간 금지
          // (값마다 클래스 재생성 → 스타일시트 누수). ControlFrame 은 var() 폴백만 갖는다.
          style={
            {
              "--c6-slider-active-start": startProgress,
              "--c6-slider-active-end": endProgress,
            } as CSSProperties
          }
        >
          <Rail aria-hidden="true" />
          {valueLabelDisplay !== "off" ? (
            <>
              <ValueLabel
                htmlFor={controlId}
                $display={valueLabelDisplay}
                style={{ left: startProgress }}
                aria-hidden="true"
              >
                {formatValue(startValue, "start")}
              </ValueLabel>
              <ValueLabel
                htmlFor={`${controlId}-end`}
                $display={valueLabelDisplay}
                style={{ left: endProgress }}
                aria-hidden="true"
              >
                {formatValue(endValue, "end")}
              </ValueLabel>
            </>
          ) : null}
          <RangeInputStack ref={stackRef} onPointerDown={handleTrackPointerDown}>
            <StackedRangeInput
              ref={setStartRef}
              id={controlId}
              name={startName}
              value={startValue}
              min={min}
              max={max}
              step={resolvedStep}
              disabled={isDisabled}
              aria-label={startAriaLabel}
              aria-valuetext={getAriaValueText?.(startValue, "start")}
              onChange={handleStartChange}
              $raised={lowerThumbRaised}
              // rest 는 primary(start) thumb 에만 적용 — 두 input 에 뿌리면
              // data-testid/id 류가 충돌하므로 단일 타깃으로 고정.
              {...rest}
            />
            <StackedRangeInput
              ref={endRef}
              id={`${controlId}-end`}
              name={endName}
              value={endValue}
              min={min}
              max={max}
              step={resolvedStep}
              disabled={isDisabled}
              aria-label={endAriaLabel}
              aria-valuetext={getAriaValueText?.(endValue, "end")}
              onChange={handleEndChange}
              $raised={!lowerThumbRaised}
            />
          </RangeInputStack>
          <SliderMarks
            marks={resolvedMarks}
            min={min}
            max={max}
            disabled={isDisabled}
            isActive={(markValue) => markValue >= startValue && markValue <= endValue}
          />
        </ControlFrame>
      </Root>
    );
  },
);

RangeSlider.displayName = "RangeSlider";
